import flatpickr from 'flatpickr/dist/flatpickr.min';
import { Italian } from 'flatpickr/dist/l10n/it';

import { goToUrl } from '../../utils/aem-utils';

import { render, prevArrowTemplate, nextArrowTemplate } from './toolbar-calendar-template';

const config = require('config').toolbarCalendar;

const CALENDAR_CLASS_NAME = '.j-calendar';
const PARENT_NODE_CLASS = 'j-archive-toolbar__date';
const METADATA_DATA_ATTRIBUTE = 'data-calendar-data';

const CALENDAR_ID = 'toolbar-calendar';

let isCalendarOpen = false;

/**
 * Months IT according to BE specifics
 */
const monthsItalian = {
  '01': 'Gennaio',
  '02': 'Febbraio',
  '03': 'Marzo',
  '04': 'Aprile',
  '05': 'Maggio',
  '06': 'Giugno',
  '07': 'Luglio',
  '08': 'Agosto',
  '09': 'Settembre',
  10: 'Ottobre',
  11: 'Novembre',
  12: 'Dicembre',
};

/**
 * Determinate if is next month active or not
 * @param {object} data data-attribute object
 */
const isNextMonthActive = (data) => {
  const today = new Date();
  const month = today.getMonth() + 1;
  const fullyear = today.getFullYear();
  return Number(data.year) < fullyear || !(month === Number(data.month) && config.maxDate === 'today');
};

/**
 * Determinate if is previous month active or not
 * @param {object} data data-attribute object
 */
const isPreviousMonthActive = (data) => {
  const today = new Date();
  const month = today.getMonth() + 1;
  const fullyear = today.getFullYear();
  return Number(data.year) < fullyear || !(month === Number(data.month) && config.minDate === 'today');
};

/**
 * Set Text in the span element
 * @param {string} calendarId
 * @param {string} text
 */
const setCalendarSpanText = (calendarId, text) => {
  const span = document.querySelector(`#${calendarId}span`);
  span.innerHTML = text;
};

/**
 * Compute calendar date according to data attribute object data
 * string to put in the span element and default date of datepicker calendar
 * @param {Object} data data attribute data-calendar-data object
 */
const computeCalendarDateString = (data) => {
  const { day, year, month } = data;
  return day ? `${day} ${monthsItalian[month]} ${year}` : 'today';
};

/**
 * Build url according to selected date
 * @param {Date} date selected date on the picker
 */
const buildUrlToJump = (date) => {
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
  const month = date.getMonth() < 9 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
  const year = `${date.getFullYear()}`;
  return `/${year}/${month}/${day}`;
};

/**
 * Go to page
 * @param {Date} selectedDate date selected on the picker
 * @param {string} baseUrl base url
 */
const gotoPage = (selectedDate, baseUrl) => {
  const url = buildUrlToJump(selectedDate);
  goToUrl(`${baseUrl}${url}`);
};

/**
 * Find parent node of the calendar
 * @param {HTMLElement} node
 */
const findParentNode = (node) =>
  node.parentNode && node.parentNode.classList.contains(PARENT_NODE_CLASS)
    ? node.parentNode
    : findParentNode(node.parentNode);

/**
 * build flatpickr calendar
 * @param {HTMLElement} calendar calendar node
 * @param {object} data data attribute object
 */
const buildCalendar = (calendar, data) => {
  return flatpickr(calendar, {
    locale: Italian,
    position: 'auto',
    dateFormat: config.dateFormat,
    static: true,
    minDate: config.minDate,
    maxDate: config.maxDate,
    mode: 'single',
    disableMobile: 'true',
    disable: [new Date()], // disable today
    defaultDate: computeCalendarDateString(data),
    prevArrow: prevArrowTemplate(),
    nextArrow: nextArrowTemplate(),
    onClose(selectedDates, dateStr, instance) {
      findParentNode(instance.element).classList.remove('is-calendar-active');
    },
    onOpen(selectedDates, dateStr, instance) {
      findParentNode(instance.element).classList.add('is-calendar-active');
    },
    onChange(selectedDates, dateStr) {
      setCalendarSpanText(calendar.getAttribute('id'), dateStr);
      gotoPage(selectedDates[0], data.baseUrl);
    },
  });
};

/**
 * Compute previous month data
 * @param {string} currentMonth  current data month
 * @param {string} currentYear current data year
 */
const computePreviousMonthAttributes = (currentMonth, currentYear) => {
  const currentMonthKey =
    Number(currentMonth) < 11 ? `0${String(Number(currentMonth) - 1)}` : String(Number(currentMonth) - 1);
  const month = monthsItalian[currentMonthKey];
  if (Number(currentMonth) === 1) {
    return {
      text: `${monthsItalian['12'].substring(0, 3)}, ${Number(currentYear) - 1}`,
      href: `/${Number(currentYear) - 1}/12/01`,
    };
  }
  return {
    text: `${month.substring(0, 3)}, ${currentYear}`,
    href: `/${currentYear}/${currentMonthKey}/01`,
  };
};

/**
 * Compute next month data
 * @param {string} currentMonth current month
 * @param {string} currentYear current year
 *
 */
const computeNextMonthAttributes = (currentMonth, currentYear) => {
  const currentMonthKey =
    Number(currentMonth) < 9 ? `0${String(Number(currentMonth) + 1)}` : String(Number(currentMonth) + 1);
  const month = monthsItalian[currentMonthKey];

  if (Number(currentMonth) === 12) {
    return {
      text: `${monthsItalian['01'].substring(0, 3)}, ${Number(currentYear) + 1}`,
      href: `/${Number(currentYear) + 1}/01/01`,
    };
  }
  return {
    text: `${month.substring(0, 3)}, ${currentYear}`,
    href: `/${currentYear}/${currentMonthKey}/01`,
  };
};

/**
 * Render Toolbar-calendar Template
 */
const renderToolbarCalendarTemplate = ($archiveToolbarNode, $archiveToolbarDataNode) => {
  const data = JSON.parse($archiveToolbarDataNode.getAttribute(METADATA_DATA_ATTRIBUTE));
  const nextAttributes = isNextMonthActive(data) ? computeNextMonthAttributes(data.month, data.year) : undefined;
  const prevAttributes = isPreviousMonthActive(data)
    ? computePreviousMonthAttributes(data.month, data.year)
    : undefined;

  const props = {
    calendarId: CALENDAR_ID,
    prevText: prevAttributes ? prevAttributes.text : '',
    prevHref: prevAttributes ? `${data.baseUrl}${prevAttributes.href}` : null,
    nextText: nextAttributes ? nextAttributes.text : '',
    nextHref: nextAttributes ? `${data.baseUrl}${nextAttributes.href}` : null,
  };
  render($archiveToolbarNode, props);
  return data;
};

/**
 * Initialyze toolbar calendar
 */
export const init = ({ $archiveToolbarNode, $archiveToolbarDataNode }) => {
  const data = renderToolbarCalendarTemplate($archiveToolbarNode, $archiveToolbarDataNode);
  const calendar = document.querySelectorAll(CALENDAR_CLASS_NAME)[0];

  const id = calendar.getAttribute('id');
  const archiveToolbarDateId = `${id}toggle`;

  const archiveToolbarSelector = `#${archiveToolbarDateId}`;
  const archiveToolbarDate = document.querySelector(archiveToolbarSelector);

  setCalendarSpanText(id, computeCalendarDateString(data));
  const flatpickrCalendar = buildCalendar(calendar, data);

  archiveToolbarDate.addEventListener('click', () => {
    isCalendarOpen ? null : flatpickrCalendar.open();
    isCalendarOpen = !isCalendarOpen;
  });

  document.addEventListener('click', (e) => {
    if (e.target !== archiveToolbarDate && e.target.closest(archiveToolbarSelector) !== archiveToolbarDate) {
      isCalendarOpen = false;
    }
  });
};
